import {ArrayBufferTarget, Muxer} from 'mp4-muxer'
import {
  VIDEO_BITRATE,
  VIDEO_CODEC_CANDIDATES,
  VIDEO_OUTPUT_SCALES,
  getPlacementSpec,
  type CreativeAnimationSettings,
  type CreativePlacementId,
} from '../constants/creativeAssetSpecs'
import {composeCreativeAsset, ensureCreativeFonts, type CreativeAssetState} from './creativeAssetRenderer'

export interface CreativeVideoSettings extends CreativeAnimationSettings {
  durationSec: number
  fps: number
}

export interface CreativeVideoOptions extends CreativeVideoSettings {
  placement: CreativePlacementId
}

/** Thrown when the browser has no WebCodecs H.264 encoder for the requested size. */
export class VideoEncodingUnsupportedError extends Error {}

/** Thrown when the caller aborts the export. */
export class VideoCancelledError extends Error {}

const KEYFRAME_INTERVAL_SEC = 2
// Frames allowed to wait inside the encoder; 4K frames are huge, so a long queue exhausts memory on weak devices.
const MAX_ENCODE_QUEUE = 3
const LOW_MEMORY_ENCODE_QUEUE = 1
const LOW_MEMORY_GB = 4
// Give the UI thread a turn at least this often (ms).
const YIELD_INTERVAL_MS = 30
// A dequeue event can be missed after an encoder error, so never wait on it for long.
const DEQUEUE_WAIT_MS = 100

/** Yields to the event loop. MessageChannel is not throttled in background tabs like setTimeout is. */
const nextTick = (): Promise<void> =>
  new Promise((resolve) => {
    const channel = new MessageChannel()
    channel.port1.onmessage = () => {
      channel.port1.close()
      resolve()
    }
    channel.port2.postMessage(null)
  })

/** Resolves once the encoder has taken a frame off its queue (or after a short timeout). */
const waitForDequeue = (encoder: VideoEncoder): Promise<void> =>
  new Promise((resolve) => {
    const done = () => {
      clearTimeout(timer)
      encoder.removeEventListener('dequeue', done)
      resolve()
    }
    const timer = setTimeout(done, DEQUEUE_WAIT_MS)
    encoder.addEventListener('dequeue', done)
  })

/** Chrome-only hint (`navigator.deviceMemory`, GB); undefined elsewhere. */
const deviceMemoryGb = (): number | undefined => (navigator as Navigator & {deviceMemory?: number}).deviceMemory

/** Keeps the screen on during a long export so the device does not sleep and throttle or kill the tab. */
const acquireWakeLock = async (): Promise<WakeLockSentinel | null> => {
  try {
    return (await navigator.wakeLock?.request('screen')) ?? null
  } catch {
    return null
  }
}

/** The encoder setup actually used: codec plus the output size (Search Results may fall back to a smaller size). */
export interface VideoOutput {
  codec: string
  scale: number
  width: number
  height: number
}

/** First supported size × H.264 level combination for a placement, largest size first. */
export const resolveVideoOutput = async (placement: CreativePlacementId, fps: number): Promise<VideoOutput> => {
  if (typeof VideoEncoder === 'undefined') throw new VideoEncodingUnsupportedError('WebCodecs unavailable')
  const spec = getPlacementSpec(placement)
  for (const scale of VIDEO_OUTPUT_SCALES[placement]) {
    const width = Math.round(spec.width * scale)
    const height = Math.round(spec.height * scale)
    for (const codec of VIDEO_CODEC_CANDIDATES) {
      const support = await VideoEncoder.isConfigSupported({
        codec,
        width,
        height,
        bitrate: VIDEO_BITRATE,
        framerate: fps,
      })
      if (support.supported) return {codec, scale, width, height}
    }
  }
  throw new VideoEncodingUnsupportedError('No H.264 encoder for this size')
}

/**
 * Renders a seamless looping H.264 MP4 frame by frame (offline, so timing is exact and the
 * result does not depend on how fast the machine can draw). `onProgress` receives 0–100.
 */
export const generateCreativeVideo = async (
  state: CreativeAssetState,
  {placement, bg, content, durationSec, fps}: CreativeVideoOptions,
  onProgress: (percent: number) => void,
  signal?: AbortSignal,
): Promise<Blob> => {
  if (signal?.aborted) throw new VideoCancelledError('aborted')
  const {codec, scale, width, height} = await resolveVideoOutput(placement, fps)
  await ensureCreativeFonts()
  const totalFrames = Math.round(durationSec * fps)

  const target = new ArrayBufferTarget()
  const muxer = new Muxer({target, video: {codec: 'avc', width, height}, fastStart: 'in-memory'})
  const failure: {error: Error | null} = {error: null}
  let encoded = 0
  const encoder = new VideoEncoder({
    output: (chunk, meta) => {
      muxer.addVideoChunk(chunk, meta)
      encoded += 1
    },
    error: (err) => {
      failure.error = err
    },
  })
  encoder.configure({codec, width, height, bitrate: VIDEO_BITRATE, framerate: fps})

  const maxQueue = (deviceMemoryGb() ?? Infinity) <= LOW_MEMORY_GB ? LOW_MEMORY_ENCODE_QUEUE : MAX_ENCODE_QUEUE
  const closeEncoder = () => {
    if (encoder.state !== 'closed') encoder.close()
  }
  // Closing the encoder also rejects a pending flush(), so a cancel is immediate.
  signal?.addEventListener('abort', closeEncoder)
  const wakeLock = await acquireWakeLock()
  let lastYield = performance.now()

  const canvas = document.createElement('canvas')
  try {
    for (let i = 0; i < totalFrames; i++) {
      if (signal?.aborted) throw new VideoCancelledError('aborted')
      if (failure.error) throw failure.error
      composeCreativeAsset(state, placement, scale, {animation: {bg, content}, t: i / totalFrames}, canvas)
      const frame = new VideoFrame(canvas, {
        timestamp: Math.round((i * 1_000_000) / fps),
        duration: Math.round(1_000_000 / fps),
      })
      encoder.encode(frame, {keyFrame: i % (fps * KEYFRAME_INTERVAL_SEC) === 0})
      frame.close()
      // Backpressure: wait for the encoder instead of piling up frames, so progress reflects real work.
      while (encoder.encodeQueueSize > maxQueue && !failure.error && !signal?.aborted) await waitForDequeue(encoder)
      onProgress((encoded / totalFrames) * 99)
      if (performance.now() - lastYield > YIELD_INTERVAL_MS) {
        await nextTick()
        lastYield = performance.now()
      }
    }
    await encoder.flush()
    if (signal?.aborted) throw new VideoCancelledError('aborted')
    if (failure.error) throw failure.error
    muxer.finalize()
    onProgress(100)
    return new Blob([target.buffer], {type: 'video/mp4'})
  } catch (err) {
    if (signal?.aborted && !(err instanceof VideoCancelledError)) throw new VideoCancelledError('aborted')
    throw err
  } finally {
    signal?.removeEventListener('abort', closeEncoder)
    closeEncoder()
    void wakeLock?.release().catch(() => undefined)
    canvas.width = 0
    canvas.height = 0
  }
}
